import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/auth';
import { Layout } from './components/Layout';
import { useRole } from './hooks/useRole';

// Core pages — loaded eagerly (always needed on first interaction)
import { LoginPage } from './pages/LoginPage';
import { RegistrationPage } from './pages/RegistrationPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { PagesListPage } from './pages/PagesListPage';
import { ProfilePage } from './pages/ProfilePage';
import { ImpressumPage } from './pages/ImpressumPage';
import { HalloPage } from './pages/HalloPage';

// Heavy pages — lazy loaded (split into separate chunks)
const PictogramLibraryPage = lazy(() => import('./pages/PictogramLibraryPage').then((m) => ({ default: m.PictogramLibraryPage })));
const UploadPage = lazy(() => import('./pages/UploadPage').then((m) => ({ default: m.UploadPage })));
const EditorPage = lazy(() => import('./pages/EditorPage').then((m) => ({ default: m.EditorPage })));
const CategoryManagementPage = lazy(() => import('./pages/CategoryManagementPage').then((m) => ({ default: m.CategoryManagementPage })));
const SourceManagementPage = lazy(() => import('./pages/SourceManagementPage').then((m) => ({ default: m.SourceManagementPage })));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage').then((m) => ({ default: m.UserManagementPage })));

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[200px]">
      <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAdmin } = useRole();
  if (!isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegistrationPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/impressum" element={<ImpressumPage />} />
      <Route path="/hallo" element={<HalloPage />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/pictograms" element={<PictogramLibraryPage />} />
                  <Route path="/pictograms/upload" element={<UploadPage />} />
                  <Route path="/editor/:pageId?" element={<EditorPage />} />
                  <Route path="/pages" element={<PagesListPage />} />
                  <Route path="/categories" element={<AdminRoute><CategoryManagementPage /></AdminRoute>} />
                  <Route path="/sources" element={<AdminRoute><SourceManagementPage /></AdminRoute>} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/admin/users" element={<UserManagementPage />} />
                  <Route path="/impressum" element={<ImpressumPage />} />
                </Routes>
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
